import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  RegulatoryNewsArticle,
  Regulator,
  RegulatoryTopic,
  REGULATORS,
  TOPICS,
  fetchRegulatoryNews,
  fetchRegulatoryNewsExports,
  RegulatoryNewsExport,
  hideRegulatoryNewsArticle,
  unhideRegulatoryNewsArticle,
  toDate,
} from '../../api/regulatoryNews';
import { RegulatoryNewsCard } from './RegulatoryNewsCard';
import { RegulatoryNewsUsageTab } from './RegulatoryNewsUsageTab';
import { NewsletterModal } from './NewsletterModal';
import { PreviousExportsModal } from './PreviousExportsModal';

// Articles rendered per "page" — keeps the feed short; filters/search still cover everything loaded.
const PAGE_SIZE = 75;

const MONTHS_ORDER = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const RegulatoryNewsSection: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [view, setView] = useState<'articles' | 'usage'>('articles');
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [previousOpen, setPreviousOpen] = useState(false);
  const [exports, setExports] = useState<RegulatoryNewsExport[]>([]);
  const [articles, setArticles] = useState<RegulatoryNewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterRegulator, setFilterRegulator] = useState<Regulator | null>(null);
  const [filterYear, setFilterYear] = useState<number | null>(null);
  const [filterMonth, setFilterMonth] = useState<string | null>(null);
  const [filterTopic, setFilterTopic] = useState<RegulatoryTopic | null>(null);
  const [searchText, setSearchText] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Super admin only: show members' feed (relevant) or the hidden/irrelevant pile.
  const [relevance, setRelevance] = useState<'relevant' | 'irrelevant'>('relevant');

  useEffect(() => {
    fetchRegulatoryNews()
      .then(setArticles)
      .catch(() => setError('Failed to load regulatory news. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  const loadExports = () => {
    fetchRegulatoryNewsExports().then(setExports).catch(() => setExports([]));
  };

  useEffect(() => {
    if (isSuperAdmin) loadExports();
  }, [isSuperAdmin]);

  const dated = useMemo(
    () => articles.map((a) => ({ article: a, date: toDate(a.publishedDate) })),
    [articles]
  );

  const availableYears = useMemo(() => {
    const source = filterMonth !== null
      ? dated.filter((d) => MONTHS_ORDER[d.date.getMonth()] === filterMonth)
      : dated;
    return [...new Set(source.map((d) => d.date.getFullYear()))].sort((a, b) => b - a);
  }, [dated, filterMonth]);

  const availableMonths = useMemo(() => {
    const source = filterYear !== null
      ? dated.filter((d) => d.date.getFullYear() === filterYear)
      : dated;
    const seen = new Set(source.map((d) => MONTHS_ORDER[d.date.getMonth()]));
    return MONTHS_ORDER.filter((m) => seen.has(m));
  }, [dated, filterYear]);

  const filteredArticles = useMemo(() => {
    return dated
      .filter(({ article, date }) => {
        if (filterRegulator !== null && article.regulator !== filterRegulator) return false;
        if (filterYear !== null && date.getFullYear() !== filterYear) return false;
        if (filterMonth !== null && MONTHS_ORDER[date.getMonth()] !== filterMonth) return false;
        if (filterTopic !== null && !article.topics.includes(filterTopic)) return false;
        if (searchText.trim()) {
          const q = searchText.toLowerCase();
          const haystack = `${article.title} ${article.summary} ${article.whyThisMatters}`.toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .map((d) => d.article);
  }, [dated, filterRegulator, filterYear, filterMonth, filterTopic, searchText]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filterRegulator, filterYear, filterMonth, filterTopic, searchText, relevance]);

  // Super admin keeps seeing hidden articles (greyed), so flip the flag in
  // place rather than removing the card.
  const setHiddenLocally = (id: string, hidden: boolean) =>
    setArticles((prev) =>
      prev.map((a) => (a.id === id ? { ...a, hidden, hiddenReason: hidden ? 'manual' : null } : a))
    );

  const handleHide = async (id: string) => {
    await hideRegulatoryNewsArticle(id);
    setHiddenLocally(id, true);
  };

  const handleUnhide = async (id: string) => {
    await unhideRegulatoryNewsArticle(id);
    setHiddenLocally(id, false);
  };

  const visibleFilteredArticles = useMemo(() => filteredArticles.filter((a) => !a.hidden), [filteredArticles]);
  const irrelevantCount = filteredArticles.length - visibleFilteredArticles.length;

  // Everyone with section access gets both piles; only the super admin can move articles between them.
  const displayedArticles = useMemo(
    () => (relevance === 'relevant' ? visibleFilteredArticles : filteredArticles.filter((a) => a.hidden)),
    [relevance, filteredArticles, visibleFilteredArticles]
  );

  const filtersActive =
    filterRegulator !== null || filterYear !== null || filterMonth !== null || filterTopic !== null || searchText.trim() !== '';

  const criteriaLabel = useMemo(() => {
    const parts: string[] = [];
    if (filterRegulator !== null) parts.push(filterRegulator);
    if (filterMonth !== null || filterYear !== null) {
      parts.push([filterMonth, filterYear].filter(Boolean).join(' '));
    }
    return parts.length > 0 ? parts.join(' · ') : 'All Regulators · All Dates';
  }, [filterRegulator, filterMonth, filterYear]);

  return (
    <>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>Regulatory Updates</h2>
          <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>
            Global regulatory news for retail CFD/spread-betting brokers and trade/transaction reporting teams
          </p>
        </div>
        {isSuperAdmin && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`btn btn-sm ${view === 'articles' ? 'btn-accent' : 'btn-outline'}`} onClick={() => setView('articles')}>Articles</button>
            <button className={`btn btn-sm ${view === 'usage' ? 'btn-accent' : 'btn-outline'}`} onClick={() => setView('usage')}>Usage</button>
            <button className="btn btn-sm btn-outline" onClick={() => setNewsletterOpen(true)}>Newsletter</button>
            <button
              className="btn btn-sm btn-outline"
              style={{ borderRadius: 999 }}
              title="Open previously generated newsletters and carousels"
              onClick={() => { loadExports(); setPreviousOpen(true); }}
            >
              Previous ({exports.length})
            </button>
          </div>
        )}
      </div>

      {isSuperAdmin && newsletterOpen && (
        <NewsletterModal
          articles={visibleFilteredArticles}
          criteriaLabel={criteriaLabel}
          onClose={() => { setNewsletterOpen(false); loadExports(); }}
        />
      )}

      {isSuperAdmin && previousOpen && (
        <PreviousExportsModal exports={exports} onClose={() => setPreviousOpen(false)} />
      )}

      {view === 'usage' ? (
        <RegulatoryNewsUsageTab />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
          <p style={{ fontSize: 15 }}>Loading regulatory news...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
          <p style={{ fontSize: 15 }}>{error}</p>
        </div>
      ) : (
        <>

      <div style={{ marginBottom: 20 }}>
        <div className="filter-bar">
          <button
            className={`filter-btn${relevance === 'relevant' ? ' active' : ''}`}
            title={isSuperAdmin ? "Members' default view" : 'Articles relevant to retail CFD brokers or trade/transaction reporting'}
            onClick={() => setRelevance('relevant')}
          >
            Relevant ({visibleFilteredArticles.length})
          </button>
          <button
            className={`filter-btn${relevance === 'irrelevant' ? ' active' : ''}`}
            title={
              isSuperAdmin
                ? 'Auto-hidden as not relevant, or hidden manually — Unhide to move to Relevant'
                : 'Articles assessed as not relevant to retail CFD brokers or trade/transaction reporting'
            }
            onClick={() => setRelevance('irrelevant')}
          >
            Not relevant ({irrelevantCount})
          </button>
        </div>

        <div className="filter-bar">
          <button className={`filter-btn${filterRegulator === null ? ' active' : ''}`} onClick={() => setFilterRegulator(null)}>All</button>
          {REGULATORS.map((r) => (
            <button
              key={r.value}
              className={`filter-btn${filterRegulator === r.value ? ' active' : ''}`}
              onClick={() => setFilterRegulator(filterRegulator === r.value ? null : r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="filter-bar">
          <button className={`filter-btn${filterYear === null ? ' active' : ''}`} onClick={() => setFilterYear(null)}>All</button>
          {availableYears.map((y) => (
            <button key={y} className={`filter-btn${filterYear === y ? ' active' : ''}`} onClick={() => setFilterYear(filterYear === y ? null : y)}>{y}</button>
          ))}
        </div>

        {availableMonths.length > 1 && (
          <div className="filter-bar">
            <button className={`filter-btn${filterMonth === null ? ' active' : ''}`} onClick={() => setFilterMonth(null)}>All</button>
            {availableMonths.map((m) => (
              <button key={m} className={`filter-btn${filterMonth === m ? ' active' : ''}`} onClick={() => setFilterMonth(filterMonth === m ? null : m)}>{m}</button>
            ))}
          </div>
        )}

        <div className="filter-bar">
          <button className={`filter-btn${filterTopic === null ? ' active' : ''}`} onClick={() => setFilterTopic(null)}>All</button>
          {TOPICS.map((t) => (
            <button
              key={t.value}
              className={`filter-btn${filterTopic === t.value ? ' active' : ''}`}
              onClick={() => setFilterTopic(filterTopic === t.value ? null : t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Search regulatory news… e.g. EMIR, leverage, transaction reporting"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '7px 12px',
            fontSize: 13,
            border: '1px solid var(--c-border)',
            borderRadius: 'var(--r)',
            background: 'var(--c-surface)',
            color: 'var(--c-text)',
            outline: 'none',
            marginTop: 4,
          }}
        />
      </div>

      {displayedArticles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--c-text-muted)' }}>
          <p style={{ fontSize: 15 }}>
            {relevance === 'irrelevant'
              ? 'No not-relevant articles for the selected filters.'
              : filtersActive ? 'No articles found for the selected filters.' : 'No regulatory news yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rn-feed">
            {displayedArticles.slice(0, visibleCount).map((article) => (
              <RegulatoryNewsCard
                key={article.id}
                article={article}
                onHide={handleHide}
                onUnhide={handleUnhide}
                canHide={isSuperAdmin}
              />
            ))}
          </div>
          {displayedArticles.length > visibleCount && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button className="btn btn-outline" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
                Show more ({displayedArticles.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </>
      )}
        </>
      )}
    </>
  );
};
